/**
 * MentorProfileForm Component
 *
 * A form that lets authenticated users opt in as a mentor and fill out
 * their mentor profile (bio, expertise, industry experience, availability,
 * preferred audience, external links, and visibility toggle).
 *
 * The form is gated behind an "I want to be a mentor" toggle switch.
 * When the switch is off, only the toggle and save button are shown.
 * When on, the full set of mentor fields appears.
 *
 * On save, comma-separated text fields (expertise, industry experience)
 * are split into arrays before being sent to the API. The parent component
 * is notified via the `onUpdate` callback on successful save.
 *
 * Rendered on the user profile page below the basic profile information.
 */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Card,
    Form,
    Button,
    Badge,
    Alert,
    Spinner,
    Row,
    Col,
} from 'react-bootstrap';

import { useUserApi } from '@/shared/services/userApi';
import { useEcosystem } from '@/shared/contexts/EcosystemContext';
import useEcosystemTaxonomy from '@/shared/hooks/useEcosystemTaxonomy';
import { AVAILABLE_FOR_OPTIONS } from '@/shared/constants/mentorConstants';
import { normalizeUrl } from '@/shared/utils/normalizeUrl';

MentorProfileForm.propTypes = {
    profile: PropTypes.shape({
        isMentor: PropTypes.bool,
        mentorProfile: PropTypes.shape({
            areasOfExpertise: PropTypes.arrayOf(PropTypes.string),
            industryExperience: PropTypes.arrayOf(PropTypes.string),
            availableFor: PropTypes.arrayOf(PropTypes.string),
            availability: PropTypes.string,
            preferredAudience: PropTypes.arrayOf(PropTypes.string),
            mentorBio: PropTypes.string,
            linkedInUrl: PropTypes.string,
            website: PropTypes.string,
            contactPhone: PropTypes.string,
            contactEmail: PropTypes.string,
            isVisible: PropTypes.bool,
        }),
    }),
    onUpdate: PropTypes.func,
    // Optional explicit ecosystem context, when rendered inside a
    // per-membership accordion the parent passes the membership's ecosystem
    // so the toggle label reads correctly. When omitted, falls back to the
    // active EcosystemContext (the single-tenant call site behavior).
    ecosystem: PropTypes.shape({
        name: PropTypes.string,
        slug: PropTypes.string,
    }),
};

export default function MentorProfileForm({ profile, onUpdate, ecosystem }) {
    const { ecosystem: activeEcosystem } = useEcosystem();
    // Prefer an explicit `ecosystem` prop (per-membership accordion use); fall
    // back to the active ecosystem context (legacy single-tenant call sites).
    const effectiveEcosystem = ecosystem || activeEcosystem;
    // Always the ACTIVE ecosystem's taxonomy: EcosystemMembershipAccordion
    // only renders this form for the active membership (see its own
    // docblock), so this never diverges from `effectiveEcosystem` above.
    const { mentorAudiences } = useEcosystemTaxonomy();
    // Form state: isMentor gates whether the detail fields are shown
    const [isMentor, setIsMentor] = useState(false);
    const [formData, setFormData] = useState({
        areasOfExpertise: '',
        industryExperience: '',
        availableFor: [],
        availability: '',
        preferredAudience: [],
        mentorBio: '',
        linkedInUrl: '',
        website: '',
        contactPhone: '',
        contactEmail: '',
        isVisible: true,
    });
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);

    const userApi = useUserApi();

    // A value already selected on the stored profile can fall outside the
    // tenant's current list (an admin edited taxonomy.mentorAudiences, or
    // the profile predates this dimension existing). Without a checkbox to
    // uncheck it, every save would 400 on that one orphaned value forever.
    // Union it in (labeled distinctly below) so the mentor can see it and
    // choose to remove it.
    const orphanedAudiences = formData.preferredAudience.filter(
        (a) => !mentorAudiences.includes(a)
    );
    const renderedAudiences = [
        ...new Set([...mentorAudiences, ...orphanedAudiences]),
    ];

    // Populate form state from the existing profile when it loads
    useEffect(() => {
        if (profile) {
            setIsMentor(profile.isMentor || false);
            if (profile.mentorProfile) {
                setFormData({
                    areasOfExpertise: (
                        profile.mentorProfile.areasOfExpertise || []
                    ).join(', '),
                    industryExperience: (
                        profile.mentorProfile.industryExperience || []
                    ).join(', '),
                    availableFor: profile.mentorProfile.availableFor || [],
                    availability: profile.mentorProfile.availability || '',
                    preferredAudience:
                        profile.mentorProfile.preferredAudience || [],
                    mentorBio: profile.mentorProfile.mentorBio || '',
                    linkedInUrl: profile.mentorProfile.linkedInUrl || '',
                    website: profile.mentorProfile.website || '',
                    contactPhone: profile.mentorProfile.contactPhone || '',
                    contactEmail: profile.mentorProfile.contactEmail || '',
                    isVisible: profile.mentorProfile.isVisible !== false,
                });
            }
        }
    }, [profile]);

    // Toggle a value in a checkbox-group array (e.g., availableFor, preferredAudience)
    const handleCheckboxArray = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: prev[field].includes(value)
                ? prev[field].filter((v) => v !== value)
                : [...prev[field], value],
        }));
    };

    // Save handler: splits comma-separated fields into arrays before sending
    const handleSave = async () => {
        setSaving(true);
        setMessage(null);

        try {
            const payload = {
                isMentor,
                mentorProfile: isMentor
                    ? {
                          areasOfExpertise: formData.areasOfExpertise
                              .split(',')
                              .map((s) => s.trim())
                              .filter(Boolean),
                          industryExperience: formData.industryExperience
                              .split(',')
                              .map((s) => s.trim())
                              .filter(Boolean),
                          availableFor: formData.availableFor,
                          availability: formData.availability,
                          preferredAudience: formData.preferredAudience,
                          mentorBio: formData.mentorBio,
                          linkedInUrl: normalizeUrl(formData.linkedInUrl),
                          website: normalizeUrl(formData.website),
                          contactPhone: formData.contactPhone,
                          contactEmail: formData.contactEmail,
                          isVisible: formData.isVisible,
                      }
                    : undefined,
            };

            await userApi.updateMentorProfile(payload);
            setMessage({ type: 'success', text: 'Mentor profile saved.' });
            if (onUpdate) onUpdate();
        } catch (err) {
            setMessage({
                type: 'danger',
                text:
                    err.response?.data?.message ||
                    'Failed to save mentor profile.',
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card className="shadow-sm mt-4">
            <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
                <strong>Mentor Profile</strong>
                {isMentor && <Badge bg="success">Active Mentor</Badge>}
            </Card.Header>
            <Card.Body className="p-4">
                {/* Success/error feedback alert */}
                {message && (
                    <Alert
                        variant={message.type}
                        dismissible
                        onClose={() => setMessage(null)}
                    >
                        {message.text}
                    </Alert>
                )}

                {/* Mentor opt-in toggle */}
                <Form.Check
                    type="switch"
                    id="mentor-toggle"
                    label={`I want to be a mentor in ${effectiveEcosystem?.name || 'this ecosystem'}`}
                    checked={isMentor}
                    onChange={(e) => setIsMentor(e.target.checked)}
                    className="mb-3 fs-5"
                />

                {/* Mentor detail fields: only shown when the toggle is on */}
                {isMentor && (
                    <>
                        <hr />

                        {/* Free-text fields: bio, expertise, industry experience */}
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Mentor Bio
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={formData.mentorBio}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        mentorBio: e.target.value,
                                    }))
                                }
                                placeholder="Describe your mentoring experience and what you can offer..."
                                maxLength={1000}
                            />
                            <Form.Text className="text-muted">
                                {formData.mentorBio.length}/1000 characters
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Areas of Expertise
                            </Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.areasOfExpertise}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        areasOfExpertise: e.target.value,
                                    }))
                                }
                                placeholder="e.g., Data Science, Robotics, Environmental Science"
                            />
                            <Form.Text className="text-muted">
                                Comma-separated list
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Industry Experience
                            </Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.industryExperience}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        industryExperience: e.target.value,
                                    }))
                                }
                                placeholder="e.g., Software Engineering, Biotech, Education"
                            />
                            <Form.Text className="text-muted">
                                Comma-separated list
                            </Form.Text>
                        </Form.Group>

                        {/* Checkbox groups: available-for and preferred audience */}
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Available For
                            </Form.Label>
                            <div className="d-flex flex-wrap gap-2">
                                {AVAILABLE_FOR_OPTIONS.map((opt) => (
                                    <Form.Check
                                        key={opt.value}
                                        type="checkbox"
                                        id={`avail-${opt.value}`}
                                        label={opt.label}
                                        checked={formData.availableFor.includes(
                                            opt.value
                                        )}
                                        onChange={() =>
                                            handleCheckboxArray(
                                                'availableFor',
                                                opt.value
                                            )
                                        }
                                    />
                                ))}
                            </div>
                        </Form.Group>

                        {renderedAudiences.length > 0 && (
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">
                                    Preferred Audience
                                </Form.Label>
                                <div className="d-flex flex-wrap gap-2">
                                    {renderedAudiences.map((audience) => (
                                        <Form.Check
                                            key={audience}
                                            type="checkbox"
                                            id={`audience-${audience}`}
                                            label={
                                                orphanedAudiences.includes(
                                                    audience
                                                )
                                                    ? `${audience} (no longer offered)`
                                                    : audience
                                            }
                                            checked={formData.preferredAudience.includes(
                                                audience
                                            )}
                                            onChange={() =>
                                                handleCheckboxArray(
                                                    'preferredAudience',
                                                    audience
                                                )
                                            }
                                        />
                                    ))}
                                </div>
                            </Form.Group>
                        )}

                        {/* Availability description and external links */}
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Availability
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={formData.availability}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        availability: e.target.value,
                                    }))
                                }
                                placeholder="e.g., Available weekday evenings, 2-3 hours per month"
                                maxLength={500}
                            />
                        </Form.Group>

                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        LinkedIn URL
                                    </Form.Label>
                                    <Form.Control
                                        type="url"
                                        value={formData.linkedInUrl}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                linkedInUrl: e.target.value,
                                            }))
                                        }
                                        placeholder="https://linkedin.com/in/..."
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        Website
                                    </Form.Label>
                                    <Form.Control
                                        type="url"
                                        value={formData.website}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                website: e.target.value,
                                            }))
                                        }
                                        placeholder="https://..."
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        Contact Phone
                                    </Form.Label>
                                    <Form.Control
                                        type="tel"
                                        value={formData.contactPhone}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                contactPhone: e.target.value,
                                            }))
                                        }
                                        placeholder="e.g., 303-555-0100"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        Contact Email
                                    </Form.Label>
                                    <Form.Control
                                        type="email"
                                        value={formData.contactEmail}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                contactEmail: e.target.value,
                                            }))
                                        }
                                        placeholder="e.g., mentor@example.com"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Visibility toggle: controls public vs. AI-only discoverability */}
                        <Form.Check
                            type="switch"
                            id="mentor-visibility"
                            label="Make my mentor profile publicly visible"
                            checked={formData.isVisible}
                            onChange={(e) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    isVisible: e.target.checked,
                                }))
                            }
                            className="mb-3"
                        />
                        <Form.Text className="text-muted d-block mb-3">
                            {formData.isVisible
                                ? 'Your mentor profile will appear in the mentor directory and AI search results.'
                                : 'Your mentor profile will only be discoverable through the AI assistant.'}
                        </Form.Text>
                    </>
                )}

                {/* Save button with loading spinner */}
                <div className="d-grid">
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        disabled={saving}
                        className="rounded-pill"
                    >
                        {saving ? (
                            <>
                                <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                />
                                Saving...
                            </>
                        ) : (
                            'Save Mentor Profile'
                        )}
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
}
